// components/MobileFloatingQuickMenu.jsx
// Itemsatış-inspired mobile right-edge floating quick menu (radial fan).
//
// - Mobile/tablet only (md-lg ≤991px). Desktop keeps FloatingQuickMenu rail.
// - Collapsed: small dark glass pill on the right edge with a chevron.
// - Open: circular shortcut buttons fan out diagonally to the left.
// - Consumes the admin-managed /quick-menu API (roleVisibility respected);
//   falls back to safe static per-role defaults when the API is empty/fails.
// - z-[990]: above page content, below bottom nav (z-997) and modals (z-1000+).
//
// TODO (admin-managed mobile): backend QuickMenuItem/Settings has no mobile
// fields yet (showOnMobile gates the OLD rail). When mobilePosition /
// mobileSortOrder / mobileIcon / mobileColor are added, consume them here.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
    FaBell, FaChevronLeft, FaChevronRight, FaCog, FaGift, FaHeadset, FaHeart,
    FaHome, FaShoppingCart, FaSignInAlt, FaStore, FaThLarge, FaUser, FaClipboardList
} from 'react-icons/fa'
import { loadQuickMenu } from '../utilies/quickMenuCache'

const ICONS = {
    cart: FaShoppingCart,
    orders: FaClipboardList,
    support: FaHeadset,
    heart: FaHeart,
    user: FaUser,
    shop: FaStore,
    home: FaHome,
    settings: FaCog,
    bell: FaBell,
    gift: FaGift,
    categories: FaThLarge,
    login: FaSignInAlt
}

// Paths where the floating menu must never appear (auth/checkout/payment/admin/seller).
const HIDDEN_PATHS = ['/login', '/register', '/forgot-password', '/oauth-success', '/shipping', '/payment']

const shouldHideOnPath = pathname =>
    HIDDEN_PATHS.some(path => pathname === path || pathname.startsWith(`${path}/`)) ||
    pathname.startsWith('/admin/') ||
    pathname.startsWith('/seller/')

const isActivePath = (pathname, target) => {
    if (target === '/') return pathname === '/'
    return pathname === target || pathname.startsWith(`${target}/`)
}

// Safe static defaults — used only when the admin API returns too few items.
// Real routes verified against App.jsx.
// `labelKey` (i18n) is used for these static fallback items only. Admin-managed
// API items have no labelKey and keep their configured label/title verbatim.
const STATIC_DEFAULTS = {
    guest: [
        { title: 'Kateqoriyalar', label: 'Kateqoriyalar', labelKey: 'quickMenu.categories', icon: 'categories', url: '/kateqoriyalar', type: 'internal', color: '#8b5cf6' },
        { title: 'Mağazalar', label: 'Mağazalar', labelKey: 'quickMenu.shops', icon: 'shop', url: '/magazalar', type: 'internal', color: '#0ea5e9' },
        { title: 'Rəsmi məhsullar', label: 'Rəsmi məhsullar', labelKey: 'quickMenu.official', icon: 'gift', url: '/official', type: 'internal', color: '#f97316' },
        { title: 'Dəstək', label: 'Dəstək', labelKey: 'quickMenu.support', icon: 'support', url: '/support', type: 'internal', color: '#10b981' },
        { title: 'Giriş', label: 'Giriş', labelKey: 'quickMenu.login', icon: 'login', url: '/login', type: 'internal', color: '#a78bfa' }
    ],
    customer: [
        { title: 'Səbət', label: 'Səbət', labelKey: 'quickMenu.cart', icon: 'cart', url: '/card', type: 'internal', color: '#8b5cf6', badgeKey: 'cart' },
        { title: 'Sifarişlər', label: 'Sifarişlər', labelKey: 'quickMenu.orders', icon: 'orders', url: '/dashboard/my-orders', type: 'internal', color: '#0ea5e9' },
        { title: 'Favorilər', label: 'Favorilər', labelKey: 'quickMenu.wishlist', icon: 'heart', url: '/dashboard/my-wishlist', type: 'internal', color: '#ef4444' },
        { title: 'Rəsmi məhsullar', label: 'Rəsmi məhsullar', labelKey: 'quickMenu.official', icon: 'gift', url: '/official', type: 'internal', color: '#f97316' },
        { title: 'Dəstək', label: 'Dəstək', labelKey: 'quickMenu.support', icon: 'support', url: '/support', type: 'internal', color: '#10b981' },
        { title: 'Hesabım', label: 'Hesabım', labelKey: 'quickMenu.account', icon: 'user', url: '/dashboard', type: 'internal', color: '#a78bfa' }
    ]
}

// Radial fan offsets (px). Tuned to stay inside a 360px viewport from the right edge.
// Items fan diagonally up + down to the left of the handle, clear of the bottom nav.
const FAN_POSITIONS = [
    { x: -78, y: -96 },
    { x: -114, y: -52 },
    { x: -132, y: 0 },
    { x: -114, y: 52 },
    { x: -78, y: 96 },
    { x: -150, y: -104 }
]

const MAX_ITEMS = FAN_POSITIONS.length

const MobileFloatingQuickMenu = () => {
    const location = useLocation()
    const { t } = useTranslation()
    const { userInfo, authChecked } = useSelector(state => state.auth)
    const { card_product_count } = useSelector(state => state.card)
    const [menu, setMenu] = useState(null)
    const [open, setOpen] = useState(false)
    const containerRef = useRef(null)

    const role = userInfo ? 'customer' : 'guest'

    useEffect(() => {
        let mounted = true
        // Deduped, session-cached fetch shared with FloatingQuickMenu (desktop).
        // Always resolves to a safe { items, settings }; static defaults below
        // still kick in when the admin feed is empty.
        loadQuickMenu().then((data) => {
            if (mounted) setMenu({ items: data.items, settings: data.settings })
        })
        return () => { mounted = false }
    }, [])

    // Close on route change.
    useEffect(() => { setOpen(false) }, [location.pathname])

    // Close on Escape + outside tap (without blocking page scroll).
    useEffect(() => {
        if (!open) return
        const onKey = e => { if (e.key === 'Escape') setOpen(false) }
        const onDown = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
        }
        document.addEventListener('keydown', onKey)
        document.addEventListener('pointerdown', onDown)
        return () => {
            document.removeEventListener('keydown', onKey)
            document.removeEventListener('pointerdown', onDown)
        }
    }, [open])

    const items = useMemo(() => {
        const apiItems = (menu?.items || [])
            .filter(item => item.roleVisibility === 'all' || item.roleVisibility === role)
        const source = apiItems.length >= 2 ? apiItems : STATIC_DEFAULTS[role]
        return source.slice(0, MAX_ITEMS)
    }, [menu, role])

    const badgeFor = useCallback(item => {
        if (item.badgeKey === 'cart' && card_product_count > 0) {
            return card_product_count > 9 ? '9+' : String(card_product_count)
        }
        return null
    }, [card_product_count])

    // Display label: translated for static fallback items (labelKey), otherwise
    // the admin-configured label/title verbatim.
    const labelOf = useCallback(
        item => (item.labelKey ? t(item.labelKey) : (item.label || item.title)),
        [t]
    )

    // Settings.isEnabled gates the whole feature (shared with the desktop rail).
    const enabled = menu?.settings ? menu.settings.isEnabled !== false : true

    if (!authChecked || !menu || !enabled || !items.length || shouldHideOnPath(location.pathname)) return null

    return (
        <div
            ref={containerRef}
            className='fixed right-0 top-[58%] z-[990] hidden -translate-y-1/2 md-lg:block'
            style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
            <div className='relative'>
                {/* Fan buttons */}
                {items.map((item, index) => {
                    const pos = FAN_POSITIONS[index]
                    const Icon = ICONS[item.icon] || FaHome
                    const active = item.type !== 'external' && isActivePath(location.pathname, item.url)
                    const badge = badgeFor(item)
                    const style = {
                        transform: open
                            ? `translate(${pos.x}px, ${pos.y}px) scale(1)`
                            : 'translate(0px, 0px) scale(0.4)',
                        opacity: open ? 1 : 0,
                        transitionDelay: `${open ? index * 45 : (items.length - index) * 30}ms`,
                        pointerEvents: open ? 'auto' : 'none',
                        color: item.color || '#a78bfa'
                    }
                    const content = (
                        <>
                            <Icon size={18} aria-hidden='true' />
                            {badge && (
                                <span className='absolute -right-1 -top-1 inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full border border-slate-950 bg-rose-500 px-1 text-[10px] font-bold text-white'>
                                    {badge}
                                </span>
                            )}
                            <span className='pointer-events-none absolute right-full mr-2 whitespace-nowrap rounded-lg bg-slate-950/90 px-2 py-1 text-[11px] font-semibold text-white opacity-0 shadow-lg transition group-hover:opacity-100 group-focus-visible:opacity-100'>
                                {labelOf(item)}
                            </span>
                        </>
                    )
                    const cls = `group absolute right-1 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-white/15 bg-slate-900/90 shadow-[0_10px_30px_rgba(15,23,42,0.5)] backdrop-blur-md transition-all duration-300 ease-out active:scale-95 ${active ? 'ring-2 ring-violet-400 shadow-[0_0_20px_rgba(139,92,246,.55)]' : ''}`

                    return item.type === 'external' ? (
                        <a
                            key={item._id || `${item.url}-${index}`}
                            href={item.url}
                            target='_blank'
                            rel='noopener noreferrer'
                            aria-label={labelOf(item)}
                            className={cls}
                            style={style}
                            onClick={() => setOpen(false)}
                        >
                            {content}
                        </a>
                    ) : (
                        <Link
                            key={item._id || `${item.url}-${index}`}
                            to={item.url}
                            target={item.openInNewTab ? '_blank' : undefined}
                            rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
                            aria-label={labelOf(item)}
                            className={cls}
                            style={style}
                            onClick={() => setOpen(false)}
                        >
                            {content}
                        </Link>
                    )
                })}

                {/* Handle */}
                <button
                    type='button'
                    onClick={() => setOpen(v => !v)}
                    aria-label={open ? t('quickMenu.closeAria') : t('quickMenu.openAria')}
                    aria-expanded={open}
                    className={`relative flex h-14 w-8 items-center justify-center rounded-l-2xl border border-r-0 border-white/15 bg-slate-900/90 text-violet-300 shadow-[0_10px_30px_rgba(15,23,42,0.5)] backdrop-blur-md transition active:scale-95 ${open ? 'shadow-[0_0_22px_rgba(139,92,246,.5)]' : ''}`}
                >
                    {open ? <FaChevronRight size={15} /> : <FaChevronLeft size={15} />}
                </button>
            </div>
        </div>
    )
}

export default MobileFloatingQuickMenu
